import { useEffect, useState } from "react";
import api, { formatMoney } from "@/lib/api";
import { Link } from "react-router-dom";
import {
  Users, Clock, CalendarCheck, ReceiptText, TrendingUp, Anchor, ArrowUpRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";

function Stat({ label, value, sub, icon: Icon, accent, testid }) {
  return (
    <div className="bg-white border border-zinc-200 p-6 rounded-sm hover:border-zinc-400 transition-colors" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-3">
            {label}
          </div>
          <div className="font-heading text-3xl font-black tracking-tight text-zinc-900">
            {value}
          </div>
          {sub && (
            <div className="mt-2 text-xs text-zinc-500">{sub}</div>
          )}
        </div>
        <div className={`h-10 w-10 grid place-items-center rounded-sm ${accent || "bg-zinc-100"}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [pendingLeaves, setPendingLeaves] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, inv, lv] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/invoices"),
          api.get("/leaves", { params: { status_filter: "pending" } }),
        ]);
        setStats(s.data);
        setRecentInvoices(inv.data.slice(0, 5));
        setPendingLeaves(lv.data.slice(0, 5));
      } catch (e) {
        // noop
      }
    })();
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-2">
            Workforce Dashboard
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tight">
            People & Operations.
          </h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">
            <span className="block md:hidden text-sm">
              Employees, attendance, HR and payroll at a glance.
            </span>
            
            <span className="hidden md:block text-sm">
              A snapshot of employees, attendance, HR operations and payroll across your organization.
            </span>
          </p>
        </div>
        <div className="flex gap-3">
          <Link to="/invoices/new">
            <Button data-testid="new-invoice-cta" className="btn-pressable rounded-sm h-11 bg-[#F97316] hover:bg-[#EA580C] text-white font-mono uppercase tracking-widest text-xs px-5">
              <ReceiptText className="h-4 w-4 mr-2" />
              Generate Invoice
            </Button>
          </Link>
          <Link to="/employees">
            <Button variant="outline" className="btn-pressable rounded-sm h-11 border-zinc-300 font-mono uppercase tracking-widest text-xs px-5">
              <Users className="h-4 w-4 mr-2" />
              Manage Crew
            </Button>
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat
          label="Total Employees"
          value={stats?.total_employees ?? "—"}
          sub={`${stats?.active_employees ?? 0} active`}
          icon={Users}
          accent="bg-zinc-100"
          testid="stat-employees"
        />
        <Stat
          label="Present Today"
          value={stats?.present_today ?? "—"}
          sub="Checked in"
          icon={Clock}
          accent="bg-emerald-100 text-emerald-700"
          testid="stat-present"
        />
        <Stat
          label="Pending Leaves"
          value={stats?.pending_leaves ?? "—"}
          sub="Awaiting approval"
          icon={CalendarCheck}
          accent="bg-amber-100 text-amber-700"
          testid="stat-leaves"
        />
        <Stat
          label="Outstanding"
          value={stats ? formatMoney(stats.outstanding, "INR") : "—"}
          sub={`${stats?.total_invoices ?? 0} invoices`}
          icon={TrendingUp}
          accent="bg-orange-100 text-orange-700"
          testid="stat-outstanding"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent invoices */}
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-sm">
          <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
            <div>
              <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500">
                Recent
              </div>
              <h3 className="font-heading text-lg font-bold tracking-tight">Invoices</h3>
            </div>
            <Link to="/invoices" className="text-xs font-mono uppercase tracking-widest text-[#F97316] hover:underline flex items-center gap-1">
              View all <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="divide-y divide-zinc-100">
            {recentInvoices.length === 0 && (
              <div className="px-6 py-12 text-center text-sm text-zinc-500">
                No invoices yet. Generate your first one.
              </div>
            )}
            {recentInvoices.map((inv) => (
              <Link
                key={inv.id}
                to={`/invoices/${inv.id}`}
                className="flex items-center justify-between px-6 py-3.5 hover:bg-zinc-50"
                data-testid={`recent-invoice-${inv.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`h-8 w-8 grid place-items-center rounded-sm ${inv.invoice_type === "shipping" ? "bg-[#09090B] text-white" : "bg-zinc-100"}`}>
                    {inv.invoice_type === "shipping" ? <Anchor className="h-4 w-4" /> : <ReceiptText className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-mono text-xs text-zinc-900 truncate">{inv.invoice_no}</div>
                    <div className="text-xs text-zinc-500 truncate">{inv.consignee_name}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm font-semibold">
                    {formatMoney(inv.grand_total, inv.currency)}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">{inv.status}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Pending leaves */}
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-6 py-4 border-b border-zinc-200">
            <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500">
              Awaiting decision
            </div>
            <h3 className="font-heading text-lg font-bold tracking-tight">Pending leaves</h3>
          </div>
          <div className="divide-y divide-zinc-100">
            {pendingLeaves.length === 0 && (
              <div className="px-6 py-12 text-center text-sm text-zinc-500">
                Nothing pending.
              </div>
            )}
            {pendingLeaves.map((lv) => (
              <Link
                key={lv.id}
                to="/leaves"
                className="block px-6 py-3.5 hover:bg-zinc-50"
                data-testid={`pending-leave-${lv.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs font-medium text-zinc-900 uppercase font-mono tracking-widest">
                    {lv.leave_type}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest font-mono text-amber-600">
                    Pending
                  </div>
                </div>
                <div className="text-xs text-zinc-500">
                  {lv.start_date} → {lv.end_date}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Currency breakdown */}
      {stats?.by_currency && Object.keys(stats.by_currency).length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-sm p-6">
          <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-4">
            Invoiced by currency
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {Object.entries(stats.by_currency).map(([cur, amt]) => (
              <div key={cur} className="border border-zinc-200 p-4 rounded-sm">
                <div className="font-mono text-[10px] tracking-widest text-zinc-500">{cur}</div>
                <div className="font-mono text-lg font-semibold">{formatMoney(amt, cur)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
